/* This example requires Tailwind CSS v2.0+ */
import { useEffect, useRef, useState } from 'react'
import { Dialog } from '@headlessui/react'
import { default as converter } from 'number-to-words'

import useBloodBalance from 'web/hooks/useBloodBalance'
import { Boon } from 'web/lib/boon'
import useMyBoons from 'web/hooks/useMyBoons'
import { BoonGalleryContext } from './GalleryBoonItems'
import useBloodShop from 'web/hooks/useBloodShop'
import useBloodAllowance from 'web/hooks/useBloodAllowance'
import usePolygonBlockNumber from 'web/hooks/usePolygonBlockNumber'
import usePoller from 'web/hooks/usePoller'
import { useSWRConfig } from 'swr'
import { CacheKey } from 'web/lib/cache-keys'
import { useWeb3React } from '@web3-react/core'
import { XIcon } from '@heroicons/react/outline'
import useBoonPrice from 'web/hooks/useBoonPrice'
import ConnectWalletButton from './ConnectWalletButton'
import useEnvironment from 'web/hooks/useEnvironment'
import AllowanceButton from './AllowanceButton'
import usePolygonBlockTime from 'web/hooks/usePolygonBlockTime'
import { PolygonAddresses } from 'web/lib/contract'

export type BoonPopupProps = {
  boon: Boon,
  onClose: any,
  context?: BoonGalleryContext
}

export default function BoonPopup({ boon, onClose, context = BoonGalleryContext.Exchange }: BoonPopupProps) {
  const { account, active } = useWeb3React()
  const { mutate } = useSWRConfig()
  const [open, setOpen] = useState(true)
  const [isBuying, setIsBuying] = useState<boolean>(false)
  const [isActivating, setIsActivating] = useState<boolean>(false)
  const [hasAllowance, setHasAllowance] = useState<boolean>(false)
  const [canPurchase, setCanPurchase] = useState<boolean>(false)
  const [theBoon, setBoon] = useState<Boon>(boon)

  const { isBeta } = useEnvironment()

  const myBoons = useMyBoons()

  // use the hook's boon which might have updated data
  useEffect(() => {
    let _boon = myBoons.findBoon(boon)
    if (_boon) {
      setBoon(_boon)
    }
  }, [myBoons])

  const totalOwned = myBoons.findBoons(boon).length

  const actionBtnRef = useRef(null)
  const bloodBalance = useBloodBalance()

  const { allowance, isUnlimitedAllowance } = useBloodAllowance(PolygonAddresses.BloodShop)
  const { handleBuyBoon, handleActivateBoon } = useBloodShop()

  const boonPrice = useBoonPrice(boon)

  useEffect(() => {
    setHasAllowance(allowance != null && isUnlimitedAllowance(allowance))
    setCanPurchase(bloodBalance >= boonPrice && allowance && isUnlimitedAllowance(allowance))
  }, [allowance, bloodBalance])

  async function onBuyClick() {
    setIsBuying(true)
    await handleBuyBoon(boon)
    setIsBuying(false)
  }

  async function onActivateClick(ev) {
    ev.preventDefault()

    setIsActivating(true)
    await handleActivateBoon(boon)
    setIsActivating(false)
    // Reload entire page bc it's hard to clear the cache of All The Things that this affects (right now)
    window.location.reload()
  }

  // Update countdown if boon is activated
  const blockNumber = usePolygonBlockNumber(false)
  const secondsPerBlock = usePolygonBlockTime()
  const poller = usePoller(1000, theBoon.isActivated)
  const [elapsedTime, setElapsedTime] = useState<number>(0)

  useEffect(() => {
    if (theBoon.isActivated && theBoon.getTimeRemaining(blockNumber, secondsPerBlock) <= 0) {
      // clear cache if time runs out
      mutate(CacheKey.myBoons(account))
    }
    setElapsedTime(elapsedTime + 1)
  }, [poller, secondsPerBlock])

  function onCloseClick(e) {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    setOpen(false)
    onClose()
  }

  return (
    <Dialog as="div"
      className="fixed z-10 inset-0 overflow-y-auto"
      initialFocus={actionBtnRef}
      open={open}
      onClose={onCloseClick}>
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-90 transition-opacity" />

        {/* This element is to trick the browser into centering the modal contents. */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>

        <div className="relative inline-block align-bottom bg-gray-800 p-4 md:p-8 rounded-lg transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="absolute right-4">
            <a href="#" onClick={onCloseClick}>
              <XIcon className="w-8 text-gray-500 hover:text-gray-300 transition-all" />
            </a>
          </div>
          <div className="flex flex-col space-y-4">

            <img src={theBoon.imageUrl} />
            <h2>{theBoon.name}</h2>
            <p className="text-gray-500 text-sm">
              {theBoon.shortDescription}
            </p>

            {context == BoonGalleryContext.Exchange && totalOwned > 0 &&
              <p>
                <span className="bg-red-500 text-white px-2 py-1 text-sm rounded-xl">
                  You own {converter.toWords(totalOwned)}
                </span>
              </p>
            }
            <p className="text-left">
              {theBoon.description}
            </p>

            {!active && <ConnectWalletButton />}

            {active && context == BoonGalleryContext.Exchange &&
              <div className="flex flex-row">
                <div className="w-1/2 text-left text-white">
                  <>Cost: <span className="font-bold">{isBeta ? boonPrice.toLocaleString() : '???'}</span> <span className="text-red-500 font-bold">$Blood</span></>
                </div>

                {/* Buying */}
                <div className="w-1/2 text-right">
                  {isBeta && canPurchase && isBuying &&
                    <button className="primaryButtonAlt uppercase" ref={actionBtnRef} disabled={true}>
                      Buying...
                    </button>
                  }

                  {isBeta && canPurchase && !isBuying &&
                    <button className="primaryButton uppercase" ref={actionBtnRef} onClick={onBuyClick}>
                      Buy
                    </button>
                  }

                  {isBeta && !canPurchase && hasAllowance &&
                    <button className="primaryButtonAlt uppercase" ref={actionBtnRef} disabled={true}>
                      Insufficient $Blood
                    </button>
                  }

                  {/* Approving */}
                  <AllowanceButton />

                  {!isBeta &&
                    <button className="primaryButtonAlt uppercase" ref={actionBtnRef}>
                      Coming soon
                    </button>
                  }
                </div>
              </div>}

            {context == BoonGalleryContext.Wallet && theBoon.activatable &&
              <div className="flex flex-row">
                {theBoon.isActivated &&
                  <button className="primaryButtonAlt w-full uppercase" ref={actionBtnRef} disabled={true}>
                    Activated (Expires in {(theBoon.getTimeRemaining(blockNumber, secondsPerBlock) - elapsedTime).timeForHumans() || '???'})
                  </button>
                }

                {isActivating &&
                  <button className="primaryButtonAlt w-full uppercase" ref={actionBtnRef} disabled={true}>
                    Activating
                  </button>
                }

                {!theBoon.isActivated && !isActivating &&
                  <button className="primaryButton w-full uppercase" ref={actionBtnRef} onClick={onActivateClick}>
                    Activate
                  </button>
                }
              </div>}

            {theBoon.activatable &&
              <p className="text-left text-gray-500 text-sm">
                Note: Boons must be activated first in order for it to take effect. Boons do not stack. Dead Gawds will automatically revive.
              </p>
            }

          </div>
        </div>
      </div>
    </Dialog>
  )
}
